import { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, Mail, Phone, Key, Shield, Award, Trophy, Flame, Medal, Crown } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const BADGE_ICONS = { medal: Medal, trophy: Trophy, crown: Crown, flame: Flame };
const TIER_COLORS = {
  bronze:   { bg: "bg-[#b08d57]/15", border: "border-[#b08d57]/60", text: "text-[#e3a86e]" },
  silver:   { bg: "bg-[#c0c7d4]/10", border: "border-[#c0c7d4]/40", text: "text-[#d7dce5]" },
  gold:     { bg: "bg-[#d4af37]/15", border: "border-[#d4af37]/60", text: "text-[#d4af37]" },
  platinum: { bg: "bg-[#e5e9f0]/10", border: "border-[#e5e9f0]/40", text: "text-[#fdfbf7]" },
};

/**
 * EmployeeProfile — the employee can update their own name, phone and
 * password. Email is read-only (changing it requires admin intervention to
 * prevent lockouts). Also shows gamification: badges + current streak.
 */
export default function EmployeeProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);
  const [gamification, setGamification] = useState(null);

  useEffect(() => {
    if (user) setForm(f => ({ ...f, name: user.name || "", phone: user.phone || "" }));
  }, [user]);

  useEffect(() => {
    api.get("/employee/stats").then(({ data }) => setGamification(data)).catch(() => { /* ignore */ });
  }, []);

  async function save(e) {
    e.preventDefault();
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error("As palavras-passe não coincidem.");
      return;
    }
    if (form.new_password && form.new_password.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres.");
      return;
    }
    setBusy(true);
    try {
      const payload = { name: form.name, phone: form.phone };
      if (form.new_password) payload.password = form.new_password;
      await api.put("/auth/me", payload);
      toast.success("Perfil atualizado.");
      setForm(f => ({ ...f, new_password: "", confirm_password: "" }));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  if (!user) return null;

  return (
    <div className="space-y-10 max-w-3xl">
      <div>
        <div className="eyebrow mb-3">Dados pessoais</div>
        <h1 className="font-display text-5xl">O meu perfil.</h1>
        <div className="text-sm text-[#a0b2af] mt-2">
          Atualize os seus dados e veja as suas conquistas profissionais.
        </div>
      </div>

      {/* Identity strip */}
      <div className="lux-card p-6 flex items-center gap-5 flex-wrap">
        <div className="w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/40 grid place-items-center">
          <User size={28} className="text-[#d4af37]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-3xl">{user.name}</div>
          <div className="text-xs text-[#a0b2af] mt-1">
            <Mail size={10} className="inline mr-1" /> {user.email}
          </div>
          <div className="text-[10px] uppercase tracking-widest mt-2 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 text-[#d4af37]">
              <Shield size={10} /> Funcionário
            </span>
            <span className={`inline-flex items-center gap-1 ${user.approved ? "text-[#10b981]" : "text-[#f59e0b]"}`}>
              <Award size={10} /> {user.approved ? "Aprovado" : "Pendente aprovação"}
            </span>
          </div>
        </div>
      </div>

      {/* GAMIFICATION — Stats grid with streak */}
      {gamification && (
        <section className="space-y-6" data-testid="gamification-panel">
          <div className="eyebrow">Conquistas</div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="lux-card p-5">
              <div className="eyebrow text-[10px]">Concluídas</div>
              <div className="font-display text-4xl text-[#d4af37] mt-2">{gamification.completed}</div>
            </div>
            <div className="lux-card p-5">
              <div className="eyebrow text-[10px]">Horas totais</div>
              <div className="font-display text-4xl mt-2">{gamification.total_hours}h</div>
            </div>
            <div className="lux-card p-5">
              <div className="eyebrow text-[10px]">Este mês</div>
              <div className="font-display text-4xl mt-2">{gamification.this_month}</div>
            </div>
            <div className="lux-card p-5 relative overflow-hidden">
              <div className="eyebrow text-[10px] flex items-center gap-1">
                <Flame size={10} className="text-[#f59e0b]" /> Sequência
              </div>
              <div className="font-display text-4xl mt-2 text-[#f59e0b]">
                {gamification.streak}
                <span className="text-sm text-[#a0b2af] ml-1">dias</span>
              </div>
              {gamification.streak >= 3 && (
                <div className="absolute -right-4 -bottom-4 text-[#f59e0b]/10 text-8xl">🔥</div>
              )}
            </div>
          </div>

          {/* Next badge progress */}
          {gamification.next_badge && (
            <div className="lux-card p-5" data-testid="next-badge">
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full border ${TIER_COLORS[gamification.next_badge.tier].border} ${TIER_COLORS[gamification.next_badge.tier].bg} grid place-items-center`}>
                  {(() => {
                    const Ic = BADGE_ICONS[gamification.next_badge.icon] || Award;
                    return <Ic size={18} className={TIER_COLORS[gamification.next_badge.tier].text} />;
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="eyebrow text-[10px]">Próxima conquista</div>
                  <div className="font-display text-xl">{gamification.next_badge.title}</div>
                  <div className="text-xs text-[#a0b2af]">{gamification.next_badge.description}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm text-[#d4af37]">
                    {gamification.next_badge.progress_value} / {gamification.next_badge.threshold}
                  </div>
                  <div className="text-[10px] text-[#a0b2af]">{gamification.next_badge.progress_pct}%</div>
                </div>
              </div>
              <div className="h-2 bg-[#1a4640] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#b08d57] via-[#d4af37] to-[#d4af37] transition-all duration-700"
                     style={{ width: `${gamification.next_badge.progress_pct}%` }}
                     data-testid="next-badge-progress" />
              </div>
            </div>
          )}

          {/* Unlocked badges */}
          <div>
            <div className="eyebrow mb-3 text-[10px]">Distintivos desbloqueados ({gamification.unlocked_badges.length})</div>
            {gamification.unlocked_badges.length === 0 ? (
              <div className="lux-card p-6 text-center text-sm text-[#a0b2af]" data-testid="no-badges">
                Ainda sem conquistas. Conclua a sua primeira limpeza para começar!
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {gamification.unlocked_badges.map(b => {
                  const Ic = BADGE_ICONS[b.icon] || Award;
                  const c = TIER_COLORS[b.tier];
                  return (
                    <div key={b.id} className={`p-4 border ${c.border} ${c.bg} rounded-sm text-center`} data-testid={`badge-${b.id}`}>
                      <Ic size={32} className={`mx-auto ${c.text}`} />
                      <div className="font-display text-base mt-2">{b.title}</div>
                      <div className="text-[10px] text-[#a0b2af] mt-1">{b.description}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Edit form */}
      <form onSubmit={save} className="lux-card p-6 space-y-5" data-testid="emp-profile-form">
        <div className="eyebrow">Editar dados</div>

        <Field icon={<User size={14} />} label="Nome completo" value={form.name}
               onChange={v => setForm(f => ({ ...f, name: v }))} testId="emp-profile-name" required />
        <Field icon={<Phone size={14} />} label="Telemóvel" value={form.phone} type="tel"
               onChange={v => setForm(f => ({ ...f, phone: v }))} testId="emp-profile-phone" />
        <Field icon={<Mail size={14} />} label="Email (apenas leitura — contacte admin)"
               value={user.email} disabled />

        <div className="pt-4 border-t border-[#1a4640] space-y-5">
          <div className="eyebrow text-[10px]">Alterar palavra-passe</div>
          <Field icon={<Key size={14} />} label="Nova palavra-passe (mín. 6 caracteres)"
                 value={form.new_password} type="password" placeholder="Deixe vazio para manter"
                 onChange={v => setForm(f => ({ ...f, new_password: v }))} testId="emp-profile-password" />
          {form.new_password && (
            <Field icon={<Key size={14} />} label="Confirmar nova palavra-passe"
                   value={form.confirm_password} type="password"
                   onChange={v => setForm(f => ({ ...f, confirm_password: v }))} testId="emp-profile-password-confirm" />
          )}
        </div>

        <button type="submit" disabled={busy} className="btn-gold px-6 py-3 text-sm disabled:opacity-60"
                data-testid="emp-profile-save">
          {busy ? "A guardar…" : "Guardar alterações"}
        </button>
      </form>
    </div>
  );
}

function Field({ icon, label, value, onChange, type = "text", testId, required = false, disabled = false, placeholder = "" }) {
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-widest text-[#a0b2af] flex items-center gap-2">
        <span className="text-[#d4af37]">{icon}</span>{label}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={e => onChange?.(e.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 outline-none focus:border-[#d4af37] disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid={testId}
      />
    </div>
  );
}
